import PartiesIcon from "@mui/icons-material/People";
import Big from "big.js";
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCase, useCaseSubmit } from "../../context/CaseContext";
import { useCurrency } from "../../context/CurrencyContext.tsx";
import { useTheme } from "../../context/ThemeContext";
import { CostType } from "../../shared/CaseConfiguration.ts";
import { CostRecovery, Party } from "../../shared/CaseModel";
import {
  CostTotals,
  fixRecoveries,
  getCostsTotal,
} from "../../shared/CaseModelUtility";
import { percentage } from "../../shared/percentage.ts";
import randomId from "../../shared/randomId.ts";
import { vatOn, vatOnCosts } from "../../shared/vat.ts";
import { byName, clientsFirst, sort } from "../analytics/AnalyticsUtility";
import CenteredMessageBox from "../general/CenteredMessageBox.tsx";
import { NO_VALUE, TABULAR_NUMERALS } from "../general/dataAtoms/shared.ts";
import { Tabs } from "../general/Tabs";
import { TonalBadge } from "../general/TonalBadge.tsx";
import { COLUMN_HEADING } from "../general/tableStyles.ts";
import { GuideHeader } from "../guide/GuideHeader.tsx";
import { GuideMarker, Marked } from "../guide/GuideMarker.tsx";
import {
  CostRecoveryCallbacks,
  CostRecoveryDetails,
} from "./CostRecoveryDetails";

export interface CostRecoveriesCallbacks {
  update(partyId: string, newValue: CostRecovery[]): Promise<void>;
}

export type RecoveriesOfParty = {
  readonly partyId: string;
  readonly partyName: string;
  recoveries: CostRecovery[];
  client?: boolean;
  // the rate of VAT the party pays and recovers with its costs
  vatRate?: number;
};

function getRecoveries({
  id,
  name,
  client,
  recoveries,
  vatRate,
}: Party): RecoveriesOfParty {
  const recoveriesPerCostType: { [type in CostType]: CostRecovery } = {
    barristerFee: { id: randomId(), type: "barristerFee", percentage: 0 },
    courtFee: { id: randomId(), type: "courtFee", percentage: 0 },
    expertFee: { id: randomId(), type: "expertFee", percentage: 0 },
    lawFirmFee: { id: randomId(), type: "lawFirmFee", percentage: 0 },
    otherDisbursement: {
      id: randomId(),
      type: "otherDisbursement",
      percentage: 0,
    },
  };

  (fixRecoveries(recoveries) ?? recoveries)?.forEach(
    ({ id, percentage, type }) => {
      recoveriesPerCostType[type] = { id, percentage, type };
    },
  );
  return {
    partyId: id,
    partyName: name,
    client,
    recoveries: Object.values(recoveriesPerCostType),
    vatRate,
  };
}

export const CostRecoveries = () => {
  const caseContext = useCase();
  const c = caseContext.getCase();

  // sort recoveries consistently with other tabs
  const recoveries: RecoveriesOfParty[] = useMemo(() => {
    const recoveriesOfParties: RecoveriesOfParty[] = [];
    sort(caseContext.getPartyDescriptors(), clientsFirst, byName).forEach(
      (partyId) => {
        const party = c.parties?.find((p) => p.id === partyId);
        if (!party) return;
        recoveriesOfParties.push(getRecoveries(party));
      },
    );
    return recoveriesOfParties;
  }, [c.parties]);

  const totalsPerParty: Map<string, CostTotals> = useMemo(() => {
    const totalsPerParty = new Map<string, CostTotals>();
    c.budgets?.forEach((budget) => {
      const total = new CostTotals();
      budget.costs.forEach((costs) => total.add(getCostsTotal(costs)));
      totalsPerParty.set(budget.partyId, total);
    });
    return totalsPerParty;
  }, [c]);

  const submit = useCaseSubmit();
  const updateParty = (party: Party) =>
    Promise.resolve(
      submit({ party: JSON.stringify(party) }, { method: "put" }),
    );

  const recoveriesCallbacks: CostRecoveriesCallbacks = {
    update: (partyId, recoveries) => {
      const party = c.parties?.find((p) => p.id === partyId);
      if (party === undefined) {
        console.warn(
          "Cannot update recoveries, no party exists with ID " + partyId,
        );
        return Promise.resolve();
      }
      return updateParty({ ...party, recoveries });
    },
  };

  return (
    <CostRecoveriesComponent
      recoveries={recoveries}
      recoveriesCallbacks={recoveriesCallbacks}
      totals={totalsPerParty}
    />
  );
};

export const CostRecoveriesComponent = ({
  recoveries,
  recoveriesCallbacks: { update },
  totals,
}: {
  recoveries: RecoveriesOfParty[];
  recoveriesCallbacks: CostRecoveriesCallbacks;
  totals: Map<string, CostTotals>;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, { keyPrefix: "case" });
  const navigate = useNavigate();
  const caseContext = useCase();
  const caseId = caseContext.getCase().id;
  // the share a slider is being dragged to, so that the total follows it
  const [sliding, setSliding] = useState<{
    partyId: string;
    type: CostType;
    percentage: number;
  }>();

  if (recoveries.length === 0) {
    return (
      <CenteredMessageBox
        message={t("recoveries.description", { count: 0 })}
        action={{
          label: t("recoveries.goToParties"),
          icon: <PartiesIcon />,
          onClick: () => navigate(`/cases/${caseId}/configuration/parties`),
        }}
      />
    );
  }

  const tabs: { label: ReactNode; tabComponent: ReactNode }[] = recoveries.map(
    ({ partyId, partyName, client, recoveries, vatRate }) => {
      const total = (totals.get(partyId) || new CostTotals()).getAll();
      // the costs as the party pays them: a party that cannot reclaim its VAT
      // recovers it with them
      const costsOf = (type: CostType) => {
        const amount =
          type === "lawFirmFee"
            ? total.lawFirmFees.toNumber()
            : total.disbursements[type].toNumber();
        return new Big(amount).add(vatOn(amount, type, vatRate)).toNumber();
      };
      const costs = total.total.add(vatOnCosts(total, vatRate)).toNumber();
      // what the rows come to, each as it is drawn
      const recovered = recoveries.reduce(
        (sum, { type, percentage }) =>
          sum +
          costsOf(type) *
            (sliding?.partyId === partyId && sliding.type === type
              ? sliding.percentage
              : percentage),
        0,
      );
      return {
        label: (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <span>{partyName}</span>
            {client && (
              <TonalBadge tone={theme.palette.primary}>
                {t("parties.clientBadge")}
              </TonalBadge>
            )}
          </Stack>
        ),
        tabComponent: (
          <Paper
            variant="outlined"
            sx={{ borderRadius: 3, overflow: "hidden", userSelect: "none" }}
          >
            <TableContainer>
              <Table
                size="small"
                sx={{
                  // the first and the last column line up with the card's inset
                  "& .MuiTableCell-root:first-of-type": { pl: 2.5 },
                  "& .MuiTableCell-root:last-of-type": { pr: 2.5 },
                }}
              >
                <TableHead>
                  <TableRow sx={{ bgcolor: "background.default" }}>
                    <TableCell sx={COLUMN_HEADING}>
                      {t("recoveries.column.type")}
                    </TableCell>
                    <TableCell sx={COLUMN_HEADING}>
                      <Marked entry="awarded">
                        {t("recoveries.column.awarded")}
                      </Marked>
                    </TableCell>
                    <TableCell align="right" sx={COLUMN_HEADING}>
                      {t("recoveries.column.rate")}
                    </TableCell>
                    <TableCell align="right" sx={COLUMN_HEADING}>
                      <Marked entry="total">{t("costs.total")}</Marked>
                    </TableCell>
                    <TableCell align="right" sx={COLUMN_HEADING}>
                      <Marked entry="recovery">
                        {t("recoveries.details.result")}
                      </Marked>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recoveries.map((recovery) => {
                    const recoveryCallbacks: CostRecoveryCallbacks = {
                      onPercentageSliding: (percentage) =>
                        setSliding({
                          partyId,
                          type: recovery.type,
                          percentage,
                        }),
                      onPercentageUpdate: (newPercentage) =>
                        update(
                          partyId,
                          recoveries.map((existing) =>
                            existing.id === recovery.id
                              ? {
                                  ...recovery,
                                  percentage: newPercentage,
                                }
                              : existing,
                          ),
                        ),
                    };
                    return (
                      <CostRecoveryDetails
                        key={recovery.type}
                        recovery={recovery}
                        recoveryCallbacks={recoveryCallbacks}
                        costsTotal={costsOf(recovery.type)}
                      />
                    );
                  })}
                  {/* what the party's costs and its recoveries come to */}
                  <TableRow sx={{ bgcolor: "background.default" }}>
                    <TableCell colSpan={2} sx={{ py: 1.5, fontWeight: 700 }}>
                      {t("budget.stage.header.total")}
                    </TableCell>
                    {/* the share of all its costs the party recovers */}
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 700, ...TABULAR_NUMERALS }}
                    >
                      {costs > 0
                        ? percentage.format(recovered / costs)
                        : NO_VALUE}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 700, ...TABULAR_NUMERALS }}
                    >
                      {currency.format(costs)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 700, ...TABULAR_NUMERALS }}
                    >
                      {currency.format(recovered)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            {vatRate !== undefined && (
              <Typography
                variant="caption"
                component="p"
                sx={{ color: "text.secondary", px: 2.5, py: 1.25 }}
              >
                {t("recoveries.withVat", {
                  name: partyName,
                  rate: percentage.format(vatRate),
                })}
              </Typography>
            )}
          </Paper>
        ),
      };
    },
  );

  return (
    <Stack spacing={1.5}>
      <GuideHeader>
        <Box>
          <Typography
            variant="h5"
            component="h1"
            sx={{ fontWeight: 700, letterSpacing: "-.01em" }}
          >
            {t("recoveries.title")}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mt: 0.5, maxWidth: "72ch" }}
          >
            {t("recoveries.description", { count: recoveries.length })}
          </Typography>
        </Box>
      </GuideHeader>
      <Box>
        <Tabs tabs={tabs} end={<GuideMarker entry="tabs" />} />
      </Box>
    </Stack>
  );
};
